import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Heart, X, MapPin, Sparkles, RotateCcw, Undo2, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, photoUrl, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useApiErrorHandler } from "@/contexts/PaywallContext";
import Confetti from "@/components/Confetti";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function toAbsolute(url) {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/photos/")) {
        const id = url.replace("/api/photos/", "");
        return photoUrl(id);
    }
    return `${BACKEND_URL}${url}`;
}

function ProfileCard({ profile, onSwipe, isTop, index }) {
    const x = useMotionValue(0);
    const rotate = useTransform(x, [-220, 0, 220], [-16, 0, 16]);
    const likeOpacity = useTransform(x, [0, 80, 160], [0, 0.5, 1]);
    const passOpacity = useTransform(x, [-160, -80, 0], [1, 0.5, 0]);
    const cover = profile.picture || (profile.photo_urls?.[0] ? toAbsolute(profile.photo_urls[0]) : null);

    const handleDragEnd = (_, info) => {
        if (!isTop) return;
        if (info.offset.x > 130) onSwipe("like");
        else if (info.offset.x < -130) onSwipe("pass");
    };

    const scale = 1 - index * 0.04;
    const yOffset = index * 10;

    return (
        <motion.div
            drag={isTop ? "x" : false}
            dragElastic={0.9}
            dragConstraints={{ left: 0, right: 0 }}
            onDragEnd={handleDragEnd}
            style={{ x, rotate, scale, y: yOffset }}
            className="absolute inset-0 rounded-4xl overflow-hidden border border-border bg-card shadow-[0_20px_60px_-25px_rgba(139,92,246,0.4)]"
            data-testid={isTop ? "swipe-card-top" : `swipe-card-${index}`}
            whileTap={isTop ? { cursor: "grabbing" } : undefined}
        >
            <div className="relative h-full w-full">
                {cover ? (
                    <img src={cover} alt={profile.name} className="absolute inset-0 h-full w-full object-cover" draggable={false} />
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-accent" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

                {/* Match % badge */}
                <div className="absolute top-4 left-4 rounded-full bg-background/90 backdrop-blur px-3 py-1.5 shadow-lg" data-testid="match-percent-badge">
                    <div className="flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-primary" strokeWidth={3} />
                        <span className="font-black text-sm text-gradient-purple">{profile.match_percent ?? 0}% match</span>
                    </div>
                </div>

                {/* LIKE / PASS overlays (only shows on top card while dragging) */}
                {isTop && (
                    <>
                        <motion.div
                            style={{ opacity: likeOpacity }}
                            className="absolute top-8 right-6 rotate-12 rounded-2xl border-4 border-emerald-400 px-4 py-1 text-emerald-400 font-black text-2xl bg-emerald-400/20 backdrop-blur"
                        >
                            LIKE
                        </motion.div>
                        <motion.div
                            style={{ opacity: passOpacity }}
                            className="absolute top-8 left-6 -rotate-12 rounded-2xl border-4 border-rose-400 px-4 py-1 text-rose-400 font-black text-2xl bg-rose-400/20 backdrop-blur"
                        >
                            PASS
                        </motion.div>
                    </>
                )}

                {/* Profile info */}
                <div className="absolute bottom-0 inset-x-0 p-5 text-white">
                    <div className="flex items-end justify-between gap-2">
                        <div className="font-display text-3xl font-black tracking-tighter" data-testid="card-name">
                            {profile.name}
                            {profile.age ? <span className="font-normal">, {profile.age}</span> : null}
                        </div>
                    </div>
                    {profile.city && (
                        <div className="mt-1 flex items-center gap-1 text-sm opacity-90">
                            <MapPin className="h-3.5 w-3.5" strokeWidth={2.5} /> {profile.city}
                        </div>
                    )}
                    {profile.bio && <p className="mt-2 text-sm opacity-90 line-clamp-2">{profile.bio}</p>}
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {(profile.vibe_tags || []).slice(0, 4).map((t) => (
                            <span
                                key={t}
                                className="rounded-full bg-white/20 backdrop-blur border border-white/30 px-2.5 py-1 text-xs font-semibold"
                                data-testid={`card-tag-${t}`}
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

function MatchModal({ match, me, onClose, onSayHi }) {
    const myAvatar = me?.picture || (me?.photos?.[0] ? photoUrl(me.photos[0]) : null);
    const themAvatar = match?.picture || (match?.photo_urls?.[0] ? toAbsolute(match.photo_urls[0]) : null);
    return (
        <AnimatePresence>
            {match && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[60] grid place-items-center bg-background/80 backdrop-blur-xl p-6"
                    data-testid="match-modal"
                >
                    <div className="blob bg-primary" style={{ width: 320, height: 320, top: 40, right: -60 }} />
                    <div className="blob" style={{ width: 260, height: 260, bottom: 40, left: -30, background: "#f0abfc" }} />

                    <motion.div
                        initial={{ scale: 0.9, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        transition={{ type: "spring", stiffness: 220, damping: 18 }}
                        className="relative z-10 w-full max-w-md rounded-4xl border-2 border-foreground bg-card p-8 text-center overflow-hidden sticker"
                    >
                        <Confetti show={true} count={30} />
                        <div className="text-xs font-black tracking-widest text-primary uppercase">✨ It's a match ✨</div>
                        <h1 className="mt-2 text-5xl sm:text-6xl font-black tracking-tighter">
                            you <span className="font-fancy text-gradient-rainbow">vibe</span>.
                        </h1>
                        <p className="mt-2 text-muted-foreground">
                            You & {match.name} both said yes. {match.match_percent}% match energy.
                        </p>

                        <div className="mt-8 flex items-center justify-center gap-3">
                            <div className="h-24 w-24 rounded-full overflow-hidden border-4 border-primary bg-accent grid place-items-center text-2xl font-black text-accent-foreground -rotate-6">
                                {myAvatar ? <img src={myAvatar} className="h-full w-full object-cover" alt="me" /> : (me?.name || "?").charAt(0).toUpperCase()}
                            </div>
                            <motion.div
                                initial={{ scale: 0, rotate: -20 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ delay: 0.3, type: "spring" }}
                                className="h-12 w-12 rounded-full bg-primary grid place-items-center text-primary-foreground"
                            >
                                <Heart className="h-6 w-6" strokeWidth={3} fill="currentColor" />
                            </motion.div>
                            <div className="h-24 w-24 rounded-full overflow-hidden border-4 border-primary bg-accent grid place-items-center text-2xl font-black text-accent-foreground rotate-6">
                                {themAvatar ? <img src={themAvatar} className="h-full w-full object-cover" alt="them" /> : (match.name || "?").charAt(0).toUpperCase()}
                            </div>
                        </div>

                        <div className="mt-8 space-y-2">
                            <Button
                                className="h-14 w-full rounded-2xl font-black text-base lift btn-shimmer"
                                onClick={onSayHi}
                                data-testid="match-modal-say-hi"
                            >
                                Say hi 💜
                            </Button>
                            <Button
                                variant="ghost"
                                className="h-12 w-full rounded-2xl font-semibold"
                                onClick={onClose}
                                data-testid="match-modal-continue"
                            >
                                Keep swiping
                            </Button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default function SwipeFeed({ me, onExit }) {
    const navigate = useNavigate();
    const handleApiError = useApiErrorHandler();
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [matchModal, setMatchModal] = useState(null);
    const [matchId, setMatchId] = useState(null);
    const [swiping, setSwiping] = useState(false);
    const [undoing, setUndoing] = useState(false);
    const [canUndo, setCanUndo] = useState(false);
    const viewedRef = useRef(new Set());

    const emptyFilters = { city: "", budget_min: "", budget_max: "", age_min: "", age_max: "" };
    const [showFilters, setShowFilters] = useState(false);
    const [availableCities, setAvailableCities] = useState([]);
    const [draft, setDraft] = useState(emptyFilters);
    const [applied, setApplied] = useState(emptyFilters);

    const activeCount = useMemo(
        () => Object.values(applied).filter((v) => v !== "" && v != null).length,
        [applied],
    );

    const load = async (filterSet = applied) => {
        setLoading(true);
        try {
            const params = { limit: 20 };
            Object.entries(filterSet).forEach(([k, v]) => {
                if (v !== "" && v != null) params[k] = v;
            });
            const { data } = await api.get("/feed", { params });
            setProfiles(data.profiles || []);
            if (data.available_cities) setAvailableCities(data.available_cities);
            viewedRef.current = new Set();
        } catch (e) {
            toast.error("Couldn't load feed");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load(emptyFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilters = () => {
        setApplied(draft);
        setShowFilters(false);
        load(draft);
    };
    const clearFilters = () => {
        setDraft(emptyFilters);
        setApplied(emptyFilters);
        setShowFilters(false);
        load(emptyFilters);
    };

    // Track "profile view" when a card becomes the top card
    useEffect(() => {
        const top = profiles[0];
        if (!top || viewedRef.current.has(top.user_id)) return;
        viewedRef.current.add(top.user_id);
        api.post("/profile-view", { target_user_id: top.user_id }).catch(() => {});
    }, [profiles]);

    const top = profiles[0];

    const handleSwipe = async (direction) => {
        if (!top || swiping) return;
        setSwiping(true);
        const target = top;
        // Remove immediately for snappy UX
        setProfiles((p) => p.slice(1));
        try {
            const { data } = await api.post("/swipe", { target_user_id: target.user_id, direction });
            setCanUndo(true);
            if (data.matched && data.match) {
                setMatchModal(data.match);
                // Fetch match_id from server for chat routing
                try {
                    const list = await api.get("/matches");
                    const mrec = (list.data.matches || []).find(
                        (m) => m.user?.user_id === data.match.user_id,
                    );
                    if (mrec) setMatchId(mrec.match_id);
                } catch {}
            }
        } catch (e) {
            // Rollback on failure
            setProfiles((p) => [target, ...p]);
            if (!handleApiError(e)) {
                toast.error("Swipe failed");
            }
        } finally {
            setTimeout(() => setSwiping(false), 200);
        }
    };

    const handleUndo = async () => {
        if (undoing || !canUndo) return;
        setUndoing(true);
        try {
            const { data } = await api.post("/swipe/undo");
            if (data.restored_profile) {
                setProfiles((p) => {
                    // Avoid duplicates if the profile is still in feed cache
                    const filtered = p.filter((x) => x.user_id !== data.restored_profile.user_id);
                    return [data.restored_profile, ...filtered];
                });
                viewedRef.current.delete(data.restored_profile.user_id);
                toast.success(
                    data.previous_direction === "like" ? "Un-liked — back on the deck" : "Rewound — give them another look",
                );
            }
            setCanUndo(false);
        } catch (e) {
            const msg = e?.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Nothing to undo");
            setCanUndo(false);
        } finally {
            setUndoing(false);
        }
    };

    // Show up to 3 cards stacked
    const stack = useMemo(() => profiles.slice(0, 3), [profiles]);

    return (
        <div className="mx-auto max-w-md px-5 pt-6" data-testid="swipe-feed">
            <div className="flex items-center justify-between">
                <button onClick={onExit} className="text-sm font-semibold text-muted-foreground hover:text-foreground" data-testid="swipe-back-btn">
                    ← Back
                </button>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowFilters((s) => !s)}
                        className={`relative rounded-full border px-3 py-1.5 text-xs font-semibold lift flex items-center gap-1 transition-colors ${
                            activeCount > 0 ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"
                        }`}
                        data-testid="swipe-filter-btn"
                    >
                        <SlidersHorizontal className="h-3.5 w-3.5" strokeWidth={2.5} /> Filters
                        {activeCount > 0 && (
                            <span className="ml-0.5 h-4 min-w-4 px-1 rounded-full bg-white text-primary text-[10px] font-black grid place-items-center" data-testid="swipe-filter-count">
                                {activeCount}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => load()}
                        className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold lift flex items-center gap-1"
                        data-testid="swipe-refresh-btn"
                    >
                        <RotateCcw className="h-3.5 w-3.5" strokeWidth={2.5} /> Refresh
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {showFilters && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                        data-testid="swipe-filter-panel"
                    >
                        <div className="mt-3 rounded-3xl border border-border bg-card p-4 space-y-3">
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">City</label>
                                <select
                                    value={draft.city}
                                    onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))}
                                    data-testid="filter-city-select"
                                    className="mt-1 h-11 w-full rounded-2xl border border-border bg-background px-3 text-sm outline-none focus:border-primary"
                                >
                                    <option value="">Any city</option>
                                    {availableCities.map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Budget (₹ / mo)</label>
                                <div className="mt-1 flex items-center gap-2">
                                    <input
                                        type="number" inputMode="numeric" min="0" placeholder="Min"
                                        value={draft.budget_min}
                                        onChange={(e) => setDraft((d) => ({ ...d, budget_min: e.target.value }))}
                                        data-testid="filter-budget-min"
                                        className="h-11 w-full rounded-2xl border border-border bg-background px-3 text-sm outline-none focus:border-primary"
                                    />
                                    <span className="text-muted-foreground text-sm">–</span>
                                    <input
                                        type="number" inputMode="numeric" min="0" placeholder="Max"
                                        value={draft.budget_max}
                                        onChange={(e) => setDraft((d) => ({ ...d, budget_max: e.target.value }))}
                                        data-testid="filter-budget-max"
                                        className="h-11 w-full rounded-2xl border border-border bg-background px-3 text-sm outline-none focus:border-primary"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Age</label>
                                <div className="mt-1 flex items-center gap-2">
                                    <input
                                        type="number" inputMode="numeric" min="18" max="99" placeholder="Min"
                                        value={draft.age_min}
                                        onChange={(e) => setDraft((d) => ({ ...d, age_min: e.target.value }))}
                                        data-testid="filter-age-min"
                                        className="h-11 w-full rounded-2xl border border-border bg-background px-3 text-sm outline-none focus:border-primary"
                                    />
                                    <span className="text-muted-foreground text-sm">–</span>
                                    <input
                                        type="number" inputMode="numeric" min="18" max="99" placeholder="Max"
                                        value={draft.age_max}
                                        onChange={(e) => setDraft((d) => ({ ...d, age_max: e.target.value }))}
                                        data-testid="filter-age-max"
                                        className="h-11 w-full rounded-2xl border border-border bg-background px-3 text-sm outline-none focus:border-primary"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center gap-2 pt-1">
                                <Button onClick={applyFilters} data-testid="filter-apply-btn" className="h-11 flex-1 rounded-2xl font-bold lift">
                                    Apply
                                </Button>
                                <Button onClick={clearFilters} variant="outline" data-testid="filter-clear-btn" className="h-11 rounded-2xl font-bold border-border">
                                    Clear
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="relative mt-4 aspect-[3/4] w-full">
                {loading ? (
                    <div className="absolute inset-0 grid place-items-center rounded-4xl border border-border bg-card">
                        <div className="animate-pulse text-muted-foreground">Finding your people…</div>
                    </div>
                ) : stack.length === 0 ? (
                    <div className="absolute inset-0 grid place-items-center rounded-4xl border border-border bg-card p-8 text-center" data-testid="swipe-empty-state">
                        <div>
                            <div className="mx-auto h-14 w-14 rounded-2xl bg-accent text-accent-foreground grid place-items-center">
                                <Sparkles className="h-7 w-7" strokeWidth={2.5} />
                            </div>
                            <div className="mt-4 font-display text-xl font-bold">You're all caught up</div>
                            <p className="mt-1 text-sm text-muted-foreground">
                                {activeCount > 0
                                    ? "No one matches these filters right now. Try loosening them."
                                    : "Come back soon — more humans joining every day."}
                            </p>
                            {activeCount > 0 ? (
                                <Button onClick={clearFilters} className="mt-5 rounded-full lift" data-testid="swipe-empty-clear-filters">
                                    Clear filters
                                </Button>
                            ) : (
                                <Button onClick={() => load()} className="mt-5 rounded-full lift" data-testid="swipe-empty-refresh">
                                    Check again
                                </Button>
                            )}
                        </div>
                    </div>
                ) : (
                    <AnimatePresence initial={false}>
                        {stack
                            .slice()
                            .reverse()
                            .map((p, idx) => {
                                const realIdx = stack.length - 1 - idx;
                                return (
                                    <ProfileCard
                                        key={p.user_id}
                                        profile={p}
                                        index={realIdx}
                                        isTop={realIdx === 0}
                                        onSwipe={handleSwipe}
                                    />
                                );
                            })}
                    </AnimatePresence>
                )}
            </div>

            {/* Action buttons */}
            <div className="mt-6 flex items-center justify-center gap-4">
                <button
                    onClick={() => handleSwipe("pass")}
                    disabled={!top || swiping}
                    data-testid="swipe-pass-btn"
                    className="h-16 w-16 rounded-full border border-border bg-card grid place-items-center lift disabled:opacity-40"
                    aria-label="Pass"
                >
                    <X className="h-7 w-7 text-rose-500" strokeWidth={3} />
                </button>
                <button
                    onClick={handleUndo}
                    disabled={!canUndo || undoing || swiping}
                    data-testid="swipe-undo-btn"
                    className="h-14 w-14 rounded-full border border-border bg-card grid place-items-center lift disabled:opacity-40"
                    aria-label="Undo last swipe"
                    title="Undo last swipe"
                >
                    <Undo2 className="h-6 w-6 text-amber-500" strokeWidth={2.8} />
                </button>
                <button
                    onClick={() => handleSwipe("like")}
                    disabled={!top || swiping}
                    data-testid="swipe-like-btn"
                    className="h-20 w-20 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-[0_15px_35px_-10px_rgba(139,92,246,0.7)] lift disabled:opacity-40"
                    aria-label="Like"
                >
                    <Heart className="h-9 w-9" strokeWidth={2.5} fill="currentColor" />
                </button>
            </div>

            <p className="mt-4 text-center text-xs text-muted-foreground">
                Swipe left to pass, right to like • Tap the amber arrow to undo
            </p>

            <MatchModal
                match={matchModal}
                me={me}
                onClose={() => {
                    setMatchModal(null);
                    setMatchId(null);
                }}
                onSayHi={() => {
                    const mid = matchId;
                    setMatchModal(null);
                    setMatchId(null);
                    if (mid) navigate(`/app/chats/${mid}`);
                    else navigate("/app/chat");
                }}
            />
        </div>
    );
}
